import { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { Modal } from 'react-bootstrap-v5';
import Swal from 'sweetalert2';
import { IPagamentoRfb, IPagamentosRfbVisao } from '../../interfaces/IModuloBancario';
import ArquivoOperacaoService from '../../services/ArquivoOperacaoService';
import ModuloBancarioService from '../../services/ModuloBancarioService';
import { BarraFiltros, PaginacaoModulo, useFiltroLista } from './paginacao-modulo';

interface Props {
    esteiraId: number;
    /** "AAAA-MM"; vazio = nenhuma competência escolhida. */
    competencia: string;
    /** A geração das minutas muda a situação dos pagamentos. */
    versaoMinutas: number;
    /** Ler um relatório ou mudar a conta de um código muda as minutas. */
    onAlterou: () => void;
}

const INTERVALO_MS = 4000;

const SITUACAO: Record<string, { texto: string; classe: string }> = {
    ANALISANDO: { texto: 'Conferindo', classe: 'badge-light' },
    AGUARDANDO_DECISAO: { texto: 'Aguardando decisão', classe: 'badge-light-warning' },
    LENDO: { texto: 'Lendo', classe: 'badge-light-primary' },
    LIDO: { texto: 'Lido', classe: 'badge-light-success' },
    DUPLICADO: { texto: 'Já enviado', classe: 'badge-light' },
    ERRO: { texto: 'Erro', classe: 'badge-light-danger' },
};

const STATUS: Record<string, { texto: string; classe: string }> = {
    CODIGO_INVALIDO: { texto: 'Código fora da tabela oficial', classe: 'badge-light-danger' },
    INFORMADO_SEM_DEBITO: { texto: 'Informado, sem débito no extrato', classe: 'badge-light-warning' },
    PAGO_EM_PARTE: { texto: 'Pendente', classe: 'badge-light-warning' },
    EM_MINUTA: { texto: 'Em minuta', classe: 'badge-light-primary' },
    LIQUIDADO: { texto: 'Liquidado', classe: 'badge-light-success' },
};

const brl = (valor: number) => valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const usd = (valor: number) => `US$ ${valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
const VAZIO: IPagamentosRfbVisao = { arquivos: [], pagamentos: [], dispensaJustificativa: null, dispensaEm: null, declaracaoDispensa: '', totalItens: 0, pagina: 1, tamanhoPagina: 50 };
const STATUS_FILTRO = Object.fromEntries(Object.entries(STATUS).map(([k, v]) => [k, v.texto]));

// DEVN3-775 — pagamentos informados pela Receita: CSV sem custo, PDF com custo antes, situação, conta do código e dispensa.
export default function PagamentosRfb({ esteiraId, competencia, versaoMinutas, onAlterou }: Props) {
    const [visao, setVisao] = useState<IPagamentosRfbVisao>(VAZIO);
    const [enviando, setEnviando] = useState(false);
    const [salvando, setSalvando] = useState(false);
    const [dispensa, setDispensa] = useState<{ justificativa: string; ciencia: boolean } | null>(null);
    const [contaCodigo, setContaCodigo] = useState<{ pagamento: IPagamentoRfb; conta: string; opcoes: { codigo: string; descricao: string }[] } | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const [ano, mes] = competencia ? competencia.split('-').map(Number) : [0, 0];
    const filtro = useFiltroLista();

    const buscar = useCallback(async (silencioso = false) => {
        if (!ano || !mes) return;
        try {
            setVisao(await ModuloBancarioService.pagamentosRfb(esteiraId, ano, mes, filtro.params));
        } catch (err: any) {
            if (!silencioso) Swal.fire({ text: err?.message ?? 'Não foi possível carregar os pagamentos RFB.', icon: 'error' });
        }
    }, [esteiraId, ano, mes, filtro.params]);

    // O filtro muda a consulta, não a competência: a lista antiga fica na tela até a nova chegar.
    useEffect(() => {
        setVisao(VAZIO);
    }, [esteiraId, ano, mes]);

    useEffect(() => {
        buscar();
    }, [buscar]);

    useEffect(() => {
        if (versaoMinutas > 0) buscar(true);
    }, [versaoMinutas, buscar]);

    const emAndamento = visao.arquivos.some(a => a.situacao === 'ANALISANDO' || a.situacao === 'LENDO');
    useEffect(() => {
        if (!emAndamento) return;
        const id = setInterval(() => buscar(true), INTERVALO_MS);
        return () => clearInterval(id);
    }, [emAndamento, buscar]);

    // Terminada a leitura, a geração refaz as minutas: a lista precisa acompanhar.
    const processavaAntes = useRef(false);
    useEffect(() => {
        if (processavaAntes.current && !emAndamento) onAlterou();
        processavaAntes.current = emAndamento;
    }, [emAndamento, onAlterou]);

    const enviar = async (ev: React.ChangeEvent<HTMLInputElement>) => {
        const files = ev.target.files;
        if (!files || files.length === 0) return;

        setEnviando(true);
        const falhas: string[] = [];
        for (const file of Array.from(files)) {
            try {
                const { signedUrl, generatedFileName } = await ArquivoOperacaoService.gerarUrl(file.name, file.type || 'application/octet-stream');
                await ArquivoOperacaoService.enviarParaStorage(signedUrl, file);
                await ModuloBancarioService.registrarPagamentoRfb(esteiraId, { ano, mes, nomeGerado: generatedFileName, nomeOriginal: file.name });
            } catch (err: any) {
                falhas.push(`${file.name}: ${err?.message ?? 'falha no envio'}`);
            }
        }
        setEnviando(false);
        if (inputRef.current) inputRef.current.value = '';
        if (falhas.length > 0) Swal.fire({ title: 'Alguns arquivos não foram enviados', html: falhas.join('<br/>'), icon: 'warning' });
        buscar();
    };

    const executar = async (acao: () => Promise<unknown>, erro: string) => {
        setSalvando(true);
        try {
            await acao();
            await buscar(true);
            return true;
        } catch (err: any) {
            Swal.fire({ text: err?.message ?? erro, icon: 'error' });
            return false;
        } finally {
            setSalvando(false);
        }
    };

    const aguardando = visao.arquivos.filter(a => a.situacao === 'AGUARDANDO_DECISAO');
    const custoProvavel = aguardando.reduce((s, a) => s + (a.custoProvavelUsd ?? 0), 0);
    const custoMaximo = aguardando.reduce((s, a) => s + (a.custoMaximoUsd ?? 0), 0);

    const decidir = (processar: boolean, ids = aguardando.map(a => a.id)) =>
        executar(() => ModuloBancarioService.decidirPagamentosRfb(esteiraId, ids, processar), 'Não foi possível registrar a decisão.');

    const confirmarDispensa = async () => {
        if (!dispensa) return;
        const ok = await executar(
            () => ModuloBancarioService.dispensarPagamentosRfb(esteiraId, { ano, mes, justificativa: dispensa.justificativa, ciencia: dispensa.ciencia }),
            'Não foi possível dispensar.');
        if (ok) setDispensa(null);
    };

    const abrirConta = async (pagamento: IPagamentoRfb) => {
        try {
            const opcoes = await ModuloBancarioService.planoContas(esteiraId);
            setContaCodigo({ pagamento, conta: pagamento.contaContabil ?? '', opcoes });
        } catch (err: any) {
            Swal.fire({ text: err?.message ?? 'Não foi possível carregar o plano de contas.', icon: 'error' });
        }
    };

    const salvarConta = async () => {
        if (!contaCodigo) return;
        const ok = await executar(
            () => ModuloBancarioService.salvarContaCodigoRfb(esteiraId, { codigoReceita: contaCodigo.pagamento.codigoReceita, contaContabil: contaCodigo.conta, ativa: true }),
            'Não foi possível salvar a conta.');
        if (ok) {
            setContaCodigo(null);
            onAlterou();
        }
    };

    const nomeArquivo = (id: number) => visao.arquivos.find(a => a.id === id)?.nomeOriginal ?? 'relatorio';

    return (
        <div className='card card-flush mb-4'>
            <div className='card-header align-items-center'>
                <h3 className='card-title fw-bold'>
                    Pagamentos RFB
                    {(enviando || emAndamento) && <span className='spinner-border spinner-border-sm ms-3'></span>}
                </h3>
                {competencia && (
                    <div className='d-flex align-items-center gap-2'>
                        {!visao.dispensaEm && (
                            <button className='btn btn-sm btn-light' onClick={() => setDispensa({ justificativa: '', ciencia: false })}>Dispensar</button>
                        )}
                        <input ref={inputRef} type='file' multiple accept='.pdf,.csv' className='d-none' onChange={enviar} />
                        <button className='btn btn-sm btn-light-primary' disabled={enviando} onClick={() => inputRef.current?.click()}>
                            <i className='fas fa-upload'></i> Enviar relatório
                        </button>
                    </div>
                )}
            </div>
            <div className='card-body pt-0'>
                {!competencia ? (
                    <div className='text-muted'>Escolha a competência no topo da página.</div>
                ) : (
                    <>
                        <div className='text-muted fs-7 mb-3'>
                            Relatório de pagamentos do e-CAC, em PDF ou CSV. O pagamento vira minuta quando fecha com os débitos dos extratos de todas as contas bancárias da empresa. Informado na Receita não é liquidado.
                        </div>

                        {visao.dispensaEm && (
                            <div className='notice bg-light rounded p-3 mb-3 fs-7'>
                                Relatório dispensado em {moment.utc(visao.dispensaEm).local().format('DD/MM/YYYY')}: {visao.dispensaJustificativa}
                            </div>
                        )}

                        {aguardando.length > 0 && (
                            <div className='d-flex flex-wrap align-items-center gap-3 border rounded p-3 mb-3'>
                                <span className='fs-7 flex-grow-1'>
                                    {aguardando.length} PDF(s) aguardando decisão. Custo provável de <strong>{usd(custoProvavel)}</strong>, no máximo {usd(custoMaximo)}.
                                </span>
                                <button className='btn btn-sm btn-light' disabled={salvando} onClick={() => decidir(false)}>Descartar</button>
                                <button className='btn btn-sm btn-primary' disabled={salvando} onClick={() => decidir(true)}>Processar</button>
                            </div>
                        )}

                        {visao.arquivos.filter(a => a.situacao !== 'LIDO').map(a => (
                            <div key={a.id} className='d-flex flex-wrap align-items-center gap-2 fs-7 mb-2'>
                                <span className={`badge ${SITUACAO[a.situacao]?.classe ?? 'badge-light'}`}>{SITUACAO[a.situacao]?.texto ?? a.situacao}</span>
                                <span>{a.nomeOriginal}</span>
                                {a.mensagem && <span className='text-muted'>{a.mensagem}</span>}
                                {(a.situacao === 'ERRO' || a.situacao === 'DUPLICADO') && (
                                    <button className='btn btn-sm btn-link p-0 ms-auto' disabled={salvando} onClick={() => decidir(false, [a.id])}>Tirar da lista</button>
                                )}
                            </div>
                        ))}

                        {(visao.totalItens > 0 || filtro.ativo) && (
                            <BarraFiltros filtro={filtro} placeholder='Buscar por código, tributo, documento ou conta' situacoes={STATUS_FILTRO} />
                        )}
                        {visao.pagamentos.length === 0 && filtro.ativo && <div className='text-muted py-4'>Nenhum pagamento com estes filtros.</div>}

                        {visao.pagamentos.length > 0 && (
                            <div className='table-responsive'>
                                <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-2 mt-3'>
                                    <thead>
                                        <tr className='fw-bold text-muted'>
                                            <th>Arrecadação</th>
                                            <th>Apuração</th>
                                            <th>Código</th>
                                            <th>Conta do tributo</th>
                                            <th className='text-end'>Principal</th>
                                            <th className='text-end'>Juros</th>
                                            <th className='text-end'>Multa</th>
                                            <th>Situação</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visao.pagamentos.map(p => (
                                            <tr key={p.id}>
                                                <td className='fs-7 text-nowrap'>{moment(p.dataArrecadacao).format('DD/MM/YYYY')}</td>
                                                <td className='fs-7 text-nowrap'>{moment(p.periodoApuracao).format('MM/YYYY')}</td>
                                                <td className='fs-7'>
                                                    {p.codigoReceita}
                                                    {p.denominacao && <div className='text-muted fs-8'>{p.denominacao}</div>}
                                                </td>
                                                <td className='fs-7'>{p.contaContabil ?? <span className='text-muted'>Sem conta</span>}</td>
                                                <td className='text-end text-nowrap fs-7'>{brl(p.principal)}</td>
                                                <td className='text-end text-nowrap fs-7'>{brl(p.juros)}</td>
                                                <td className='text-end text-nowrap fs-7'>{brl(p.multa)}</td>
                                                <td className='fs-7'><span className={`badge ${STATUS[p.situacao]?.classe ?? 'badge-light'}`}>{STATUS[p.situacao]?.texto ?? p.situacao}</span></td>
                                                <td className='text-end text-nowrap'>
                                                    {p.situacao !== 'CODIGO_INVALIDO' && (
                                                        <button className='btn btn-sm btn-light me-1' disabled={salvando} onClick={() => abrirConta(p)}>Conta</button>
                                                    )}
                                                    <button className='btn btn-sm btn-icon btn-light' title={`Abrir ${nomeArquivo(p.arquivoId)}`}
                                                        onClick={() => ModuloBancarioService.baixarPagamentoRfb(esteiraId, p.arquivoId, nomeArquivo(p.arquivoId))
                                                            .catch((err: any) => Swal.fire({ text: err?.message ?? 'Não foi possível abrir o arquivo.', icon: 'error' }))}>
                                                        <i className='fas fa-file-download'></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        <PaginacaoModulo total={visao.totalItens} pagina={filtro.pagina} tamanhoPagina={filtro.tamanhoPagina} onPagina={filtro.setPagina} onTamanho={filtro.setTamanho} />
                    </>
                )}
            </div>

            {contaCodigo && (
                <Modal show onHide={() => setContaCodigo(null)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Conta do código {contaCodigo.pagamento.codigoReceita}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className='text-muted fs-7 mb-3'>{contaCodigo.pagamento.denominacao} Vale para toda a empresa. As provisões são buscadas nesta conta, e as minutas regeneráveis são refeitas.</div>
                        <label className='form-label'>Conta do tributo a recolher</label>
                        <select className='form-select' value={contaCodigo.conta} onChange={e => setContaCodigo({ ...contaCodigo, conta: e.target.value })}>
                            <option value=''>Escolha a conta</option>
                            {contaCodigo.opcoes.map(c => <option key={c.codigo} value={c.codigo}>{c.codigo} — {c.descricao}</option>)}
                        </select>
                    </Modal.Body>
                    <Modal.Footer>
                        <button className='btn btn-light' onClick={() => setContaCodigo(null)}>Cancelar</button>
                        <button className='btn btn-primary' disabled={salvando || !contaCodigo.conta} onClick={salvarConta}>Salvar</button>
                    </Modal.Footer>
                </Modal>
            )}

            {dispensa && (
                <Modal show onHide={() => setDispensa(null)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Dispensar relatório RFB</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className='text-muted fs-7 mb-3'>Vale para toda a empresa nesta competência. As minutas e o fechamento não mudam.</div>
                        <label className='form-label'>Justificativa</label>
                        <textarea className='form-control mb-3' rows={3} maxLength={500} value={dispensa.justificativa}
                            onChange={e => setDispensa({ ...dispensa, justificativa: e.target.value })}></textarea>
                        <label className='form-check form-check-custom'>
                            <input type='checkbox' className='form-check-input' checked={dispensa.ciencia} onChange={e => setDispensa({ ...dispensa, ciencia: e.target.checked })} />
                            <span className='form-check-label fs-7'>{visao.declaracaoDispensa}</span>
                        </label>
                    </Modal.Body>
                    <Modal.Footer>
                        <button className='btn btn-light' onClick={() => setDispensa(null)}>Cancelar</button>
                        <button className='btn btn-primary' disabled={salvando || !dispensa.ciencia || !dispensa.justificativa.trim()} onClick={confirmarDispensa}>Dispensar</button>
                    </Modal.Footer>
                </Modal>
            )}
        </div>
    );
}
